import { FormJourneyItem } from "@plura/shared/types";
import { stripHtmlTags } from "@plura/shared/utils";
import { wrapMentionsWithBadges } from "./wrapMentionsWithBadges";

interface DynamicQuestionContentParams {
  content: string
  score: number | null
  editMode?: boolean
  previewMode?: boolean
  hiddenFields?: {
    field: string;
    value: string;
  }[],
  journeyItems: FormJourneyItem[]
  userResponses?: { [key: string]: string | number | boolean | null }
}

export const dynamicQuestionContent = ({
  content,
  score,
  editMode = false,
  previewMode = false,
  hiddenFields = [],
  journeyItems,
  userResponses = {},
}: DynamicQuestionContentParams) => {
  let processedContent = content

  if (editMode && !previewMode) {
    // Use the utility function to wrap mentions with badges
    processedContent = wrapMentionsWithBadges({
      content: processedContent,
      hiddenFields,
      journeyItems
    })
  } else {
    // Replace @score with actual value
    processedContent = processedContent.replace(/@score/g, score?.toString() ?? '0')

    // Replace hidden fields with actual values (or mask them in preview mode)
    hiddenFields.forEach(({ field, value }) => {
      // Ensure the field starts with @ for proper matching
      const fieldWithAt = field.startsWith('@') ? field : `@${field}`
      const escapedField = fieldWithAt.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      const regex = new RegExp(escapedField, 'g')
      // In preview mode, show xxxxx instead of the actual value
      processedContent = processedContent.replace(regex, previewMode ? 'xxxxx' : value)
    })

    // Helper: generate a filler placeholder for preview mode when no user response exists
    const toTitleCase = (fieldName: string): string => {
      return fieldName
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    }

    const getPreviewFiller = (blockId: string, fieldName?: string): string => {
      if (!previewMode) return ''
      // In preview mode, show a visible placeholder so recall references aren't invisible
      const journeyItem = journeyItems.find(item => item.blockId === blockId)
      if (fieldName) {
        return `<span class="fillerBorder">${toTitleCase(fieldName)}</span>`
      }
      if (journeyItem) {
        // Plain-text slice — content may carry inline formatting tags, and a
        // raw substring could cut through the middle of one
        const label = stripHtmlTags(journeyItem.content).substring(0, 20) || 'answer'
        return `<span class="fillerBorder">${label}</span>`
      }
      return '<span class="fillerBorder"></span>'
    }

    // Handle journey item references {{blockId}} and {{blockId__fieldName}}
    processedContent = processedContent.replace(/\{\{([^}]+)\}\}/g, (match, reference) => {
      // Check if this is a subfield reference (contains __)
      if (reference.includes('__')) {
        const [blockId, fieldName] = reference.split('__')

        // First try to get the field value using the form field ID format: blockId_fieldName
        const fieldKey = `${blockId}_${fieldName}`
        const fieldValue = userResponses[fieldKey]

        if (fieldValue) {
          return fieldValue.toString()
        }

        // Fallback: try to get from nested object structure (if it exists)
        const userResponse = userResponses[blockId]
        if (userResponse && typeof userResponse === 'object' && userResponse !== null) {
          const nestedFieldValue = (userResponse as Record<string, unknown>)[fieldName]
          return nestedFieldValue ? nestedFieldValue.toString() : getPreviewFiller(blockId, fieldName)
        }

        return getPreviewFiller(blockId, fieldName)
      } else {
        // Regular blockId reference
        const userResponse = userResponses[reference]
        if (!userResponse) return getPreviewFiller(reference)

        // Find the journey item to check if it has selectOptions (multiple choice, dropdown, etc.)
        const journeyItem = journeyItems.find(item => item.blockId === reference)
        if (journeyItem && journeyItem.selectOptions && journeyItem.selectOptions.length > 0) {
          const responseString = userResponse.toString()

          // Only apply comma-splitting logic for MULTIPLE_CHOICE and DROPDOWN types
          if ((journeyItem.type === 'MULTIPLE_CHOICE' || journeyItem.type === 'DROPDOWN') && responseString.includes(',')) {
            // Split the comma-separated IDs and convert each to its label
            const selectedIds = responseString.split(',').map(id => id.trim())
            const selectedLabels = selectedIds
              .map(id => {
                const option = journeyItem.selectOptions?.find(opt => opt.id === id)
                return option ? option.value : id
              })
              .filter(label => label) // Remove any empty/null values

            return selectedLabels.join(', ')
          } else {
            // Single selection or other types - find the option label by ID
            const selectedOption = journeyItem.selectOptions.find(option => option.id === responseString)
            return selectedOption ? selectedOption.value : responseString
          }
        }

        // For non-select questions, return the raw value
        return userResponse.toString()
      }
    })
  }

  return processedContent
}